import Typography from '@mui/material/Typography';
import { ViewRoot, ViewContent, ViewContentCenter, ViewSidebar } from '../../layout/Main/styled';
import Stepper from '../../components/molecules/Stepper';
import ShoppingCart from '../../components/molecules/ShoppingCart';
import Schedules from '../../components/molecules/Schedules';
import { WrapperStepper } from './styled';
import { IProductResult, useGetProductDetailsMutation } from '../../services/product.service';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveProductDetail } from '../../features/product/product-slice';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { clickNextStep, resetStepper } from '../../features/stepper/stepper-slice';
import { saveTotalToPay } from '../../features/payment/payment-slice';
import { RootState } from '../../store';
import AlertMessage from '../../components/atoms/AlertMessage';
import { orderServiceSteps } from '../../utils';
import {
  Calendar,
  ECalendarType,
  saveAvailableCalendars,
  saveCalendarSelected
} from '../../features/calendar/calendar-slice';
import { persistor } from '../..';
import { createScheduleLandingPage } from '../../features/queryString/queryString-slice';
import { EcommerceEventTrackingInstance } from '../../lib/gtm/EcommerceEventTracking';
import { WrapperActions } from '../../components/molecules/ManagerStepper/styled';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { customEventTracking } from '../../lib/gtm';

const Schedule = () => {
  const { productId, calendarIdURL, userId } = useParams();
  const { product, nextPage, schedule } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasBackButton } = location.state ?? {};
  const [getProductDetails, { data, isLoading }] = useGetProductDetailsMutation();
  const filterCalendarByTypes = (product: IProductResult, calendarIdByURL: string): string[] => {
    const calendarFilteredByType = product.calendarTypes.filter(
      item => item.type === ECalendarType.MAIN || item.type === ECalendarType.BOTH
    );
    if (calendarIdByURL) {
      return [calendarIdByURL];
    } else if (calendarFilteredByType) {
      return calendarFilteredByType.map(item => item.calendarId);
    } else {
      return [product.calendars[0].id];
    }
  };

  const createLandingURL = ({
    productId,
    calendarIdURL,
    userId
  }: {
    productId: string;
    calendarIdURL?: string;
    userId?: string;
  }): void => {
    const url = `/servico/${productId}${calendarIdURL ? `/${calendarIdURL}` : ''}${userId ? `/especialista/${userId}` : ''}`;
    dispatch(createScheduleLandingPage(url));
  };

  const isValidStep = (): boolean => {
    const { currentStep, validSteps } = nextPage;
    if (validSteps[currentStep] === true) {
      return true;
    }
    return false;
  };

  const handleNextPage = () => {
    dispatch(clickNextStep());
    if (nextPage.currentStep === 'schedule') {
      navigate(nextPage.url);
      customEventTracking.send({
        event: 'journey_schedule_selected',
        props: {
          schedule: schedule.dateStart
        }
      });
    }
  };

  useEffect(() => {
    persistor.purge();
    dispatch(saveCalendarSelected({ calendar: {} as Calendar, doctorId: undefined }));
    dispatch(resetStepper());
    dispatch(saveAvailableCalendars([]));
    getProductDetails({ id: productId, doctorId: userId });
    createLandingURL({ productId: productId || '', calendarIdURL, userId });
  }, []);

  useEffect(() => {
    if (data) {
      if (data.active === false) {
        navigate('/servico-indisponivel');
      }
      EcommerceEventTrackingInstance.sendViewItemEvent({
        productId: data.id || '',
        productName: data.name || '',
        productPrice: data.price || 0
      });

      dispatch(
        saveProductDetail({
          ...product,
          id: data.id,
          services: data.services,
          servicesSteps: orderServiceSteps(data.servicesSteps),
          price: data.price,
          isNeedAddress: data.isNeedAddress,
          installment: data.maxInstallment,
          urlAnamnese: data.urlAnamnese,
          sendAnamnese: data.sendAnamnese,
          name: data.name,
          alias: data.alias,
          healthPlan: data.healthPlan,
          durationTime: data.durationTime,
          doctorName: data.doctorName,
          hasTimeLimitSchedule: data.hasTimeLimitSchedule
        })
      );
      dispatch(saveTotalToPay(data.price));
      const calendarTypes: string[] = filterCalendarByTypes(data, calendarIdURL || '');
      const calendarAvailables = data.calendars.filter(item => {
        if (calendarTypes.includes(item.id)) return item;
      });
      dispatch(saveAvailableCalendars(calendarAvailables));
      if (calendarAvailables.length === 1 || (userId && userId.length > 0)) {
        dispatch(saveCalendarSelected({ calendar: calendarAvailables[0], doctorId: userId }));
      }
    }
  }, [data]);

  return (
    <>
      <ViewRoot>
        <ViewContent className="ViewContent">
          <ViewContentCenter>
            <WrapperStepper>
              <Stepper separator="de" text="Escolha de data e hora" />
            </WrapperStepper>
            <Typography variant="h3">Escolha o melhor horário para o seu atendimento</Typography>
            {isLoading ? <p>Carregando...</p> : <Schedules />}
            <WrapperActions sx={{ justifyContent: hasBackButton ? 'space-between' : 'flex-end' }}>
              {hasBackButton && (
                <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                  <ArrowBackIcon />
                  <Typography variant="body1" sx={{ ml: 1.5 }}>
                    Voltar
                  </Typography>
                </Button>
              )}

              <LoadingButton
                loading={nextPage.isLoading}
                variant="contained"
                onClick={handleNextPage}
                disabled={!isValidStep()}
                sx={{ padding: '12px 16px 14px 24px' }}
              >
                <Typography variant="body1" sx={{ mr: 1.5 }}>
                  Continuar
                </Typography>
                <ArrowForwardIcon />
              </LoadingButton>
            </WrapperActions>
            {!isLoading && !product.isServiceAvailableCityFromZipCode && (
              <AlertMessage
                noTimer
                oneOpen="true"
                icon={false}
                text={
                  <>
                    A coleta da Oya ainda não está disponível em sua região. Para realizar a consulta, você precisará ter o{' '}
                    <br />
                    resultado do exame AMH em mãos realizado no laboratório de sua preferência.
                  </>
                }
                textButton="Ok, entendi"
                type="success"
              />
            )}
          </ViewContentCenter>
        </ViewContent>
        <ViewSidebar component="aside" className="ViewSidebar">
          {isLoading ? <p>Carregando...</p> : <ShoppingCart />}
        </ViewSidebar>
      </ViewRoot>
    </>
  );
};

export default Schedule;
